import {ThemeProvider} from '@mui/material/styles';
import {createBrowserRouter, Route, RouterProvider, Routes, ScrollRestoration} from 'react-router-dom';
import {HelmetProvider} from 'react-helmet-async';
import createCache from '@emotion/cache';
import {CacheProvider} from '@emotion/react';
import {Suspense} from 'react';
import {ApolloProvider} from '@apollo/client';
import {PostHogProvider} from '@posthog/react';

import {POSTHOG_TOKEN, POSTHOG_HOST} from 'config';
import {RootStyles} from 'core/RootStyles';
import {RouterErrorBoundary} from 'core/RouterErrorBoundary';
import {IMPERSONATION, PDF_REPORTS} from 'core/constants/paths';
import {themeDecisions} from 'core/themes/themeDecisions';
import App from 'core/App';
import {decisionsGraphQLClient} from 'modules/decisionsNext/api';

import {CheckBrowserSupport} from 'components/UnsupportedBrowserWarning/CheckBrowserSupport';
import {LoadingScreen} from 'components/LoadingScreen';

import {impersonationToken} from './impersonation';

import {AuthProvider} from 'core/components/AuthProvider';
import {AuthorizedArea} from 'core/components/AuthorizedArea';
import {DecisionsDisabledGate} from 'core/components/DecisionsDisabledGate';
import {UserInfoProvider} from 'core/components/UserInfoProvider';
import PDFReportsRoot from 'modules/pdfReports/components/Root';

const cache = createCache({
  key: 'css',
  prepend: true,
});

cache.compat = true;

const router = createBrowserRouter(
  [
    {
      path: '/*',
      element: (
        <CacheProvider value={cache}>
          <ThemeProvider theme={themeDecisions}>
            <Suspense fallback={<LoadingScreen />}>
              <CheckBrowserSupport>
                <Routes>
                  <Route path={`${PDF_REPORTS}/*`} element={<PDFReportsRoot />} />

                  <Route
                    path='*'
                    element={
                      <AuthProvider>
                        <AuthorizedArea>
                          <UserInfoProvider>
                            <DecisionsDisabledGate>
                              <App />
                            </DecisionsDisabledGate>
                          </UserInfoProvider>
                        </AuthorizedArea>
                      </AuthProvider>
                    }
                  />
                </Routes>
                <ScrollRestoration getKey={location => location.pathname} />
              </CheckBrowserSupport>
            </Suspense>
          </ThemeProvider>
        </CacheProvider>
      ),
      errorElement: <RouterErrorBoundary />,
    },
  ],
  {
    basename: impersonationToken ? `/${IMPERSONATION}/${impersonationToken}` : undefined,
    future: {
      v7_skipActionErrorRevalidation: false,
      v7_fetcherPersist: false,
      v7_normalizeFormMethod: false,
      v7_partialHydration: false,
    },
  }
);

const Root = () => {
  return (
    <ApolloProvider client={decisionsGraphQLClient}>
      <RootStyles />
      <HelmetProvider>
        <PostHogProvider
          apiKey={POSTHOG_TOKEN}
          options={{
            api_host: POSTHOG_HOST,
            defaults: '2026-01-30',
            opt_out_capturing_by_default: true,
            capture_pageview: 'history_change', // For SPA
            // Impersonation opens a new tab on the same origin, which by default shares
            // one PostHog storage bucket with the origin-user tab. That lets the two tabs
            // clobber each other's distinct_id / super properties in localStorage and
            // pollutes the identity graph. Give impersonation tabs an isolated persistence
            // namespace so they never collide with the origin user's session.
            ...(impersonationToken && {persistence_name: `impersonation_${impersonationToken}`}),
          }}
        >
          <RouterProvider
            router={router}
            future={{
              v7_startTransition: false,
              // @ts-ignore -- it should be placed in the browser router according to the docs, but you know :)
              v7_relativeSplatPath: false,
            }}
          />
        </PostHogProvider>
      </HelmetProvider>
    </ApolloProvider>
  );
};

export default Root;
